<script>
import axios from '@/utils/axios';
import { Offcanvas } from 'bootstrap';

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default {
  data() {
    return {
      loading: false,
      slip: null,
      oc: null,
    };
  },
  computed: {
    period() {
      if (!this.slip) return '';
      return `${MONTHS[this.slip.month] || this.slip.month} ${this.slip.year}`;
    },
  },
  methods: {
    money(v) {
      const n = parseFloat(v);
      if (isNaN(n)) return '₹0';
      return '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
    },
    num(v) { return parseFloat(v) || 0; },

    async open(employeeId, month, year) {
      this.slip = null;
      this.oc = Offcanvas.getOrCreateInstance(this.$refs.oc);
      this.oc.show();
      this.loading = true;
      try {
        const res = await axios.get(`/hrms/payroll/payslip/${employeeId}`, {
          params: { month, year },
        });
        this.slip = res.data.data;
      } catch (e) {
        this.$swalToast(e.message, 'error');
        this.close();
      } finally {
        this.loading = false;
      }
    },
    close() { this.oc?.hide(); },
  },
};
</script>

<template>
  <div ref="oc" class="offcanvas offcanvas-end ps-oc" tabindex="-1" id="hrm_payslip_modal">
    <div class="offcanvas-header">
      <div class="d-flex align-items-center gap-2">
        <span class="ps-oc-ico"><i class="ti ti-file-invoice"></i></span>
        <h5 class="offcanvas-title mb-0">Payslip</h5>
      </div>
      <button type="button" class="btn-close" data-bs-dismiss="offcanvas"></button>
    </div>

    <div class="offcanvas-body">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary"></div>
      </div>

      <template v-else-if="slip">
        <!-- Employee + period -->
        <div class="ps-head">
          <div class="ps-emp-name">{{ slip.employee?.name }}</div>
          <div class="ps-emp-sub">{{ slip.employee?.email }}</div>
          <div class="ps-period">{{ period }}</div>
        </div>

        <!-- Attendance summary -->
        <div class="ps-att">
          <div class="ps-att-item">
            <span class="ps-att-num">{{ num(slip.working_days) }}</span>
            <span class="ps-att-lbl">Working</span>
          </div>
          <div class="ps-att-item">
            <span class="ps-att-num" style="color:#16a34a;">{{ num(slip.present_days) }}</span>
            <span class="ps-att-lbl">Present</span>
          </div>
          <div class="ps-att-item">
            <span class="ps-att-num" style="color:#dc2626;">{{ num(slip.absent_days) }}</span>
            <span class="ps-att-lbl">Absent</span>
          </div>
          <div class="ps-att-item">
            <span class="ps-att-num" style="color:#ea580c;">{{ num(slip.paid_leave_days) + num(slip.unpaid_leave_days) }}</span>
            <span class="ps-att-lbl">Leave</span>
          </div>
          <div class="ps-att-item">
            <span class="ps-att-num" style="color:#d97706;">{{ num(slip.late_count) }}</span>
            <span class="ps-att-lbl">Late</span>
          </div>
          <div class="ps-att-item">
            <span class="ps-att-num" style="color:#2563eb;">{{ num(slip.holiday_days) }}</span>
            <span class="ps-att-lbl">Holidays</span>
          </div>
        </div>

        <!-- Earnings -->
        <div class="ps-section">
          <div class="ps-sec-title ps-earn"><i class="ti ti-trending-up"></i>Earnings</div>
          <div class="ps-rows">
            <div v-for="(e, i) in slip.earnings" :key="'e'+i" class="ps-row">
              <div class="ps-row-left">
                <span class="ps-row-name">{{ e.component_name }}</span>
              </div>
              <span class="ps-row-amt">{{ money(e.amount) }}</span>
            </div>
          </div>
          <div class="ps-total">
            <span>Total Earnings</span>
            <span style="color:#16a34a;">{{ money(slip.total_earnings) }}</span>
          </div>
        </div>

        <!-- Deductions -->
        <div class="ps-section">
          <div class="ps-sec-title ps-deduct"><i class="ti ti-trending-down"></i>Deductions</div>
          <div class="ps-rows">
            <div v-for="(d, i) in slip.deductions" :key="'d'+i" class="ps-row">
              <div class="ps-row-left">
                <span class="ps-row-name">{{ d.component_name }}</span>
              </div>
              <span class="ps-row-amt">{{ money(d.amount) }}</span>
            </div>

            <!-- Unpaid / Absent -->
            <div v-if="num(slip.unpaid_deduction) > 0" class="ps-row">
              <div class="ps-row-left">
                <span class="ps-row-name">Unpaid / Absent</span>
                <span class="ps-row-sub">{{ num(slip.absent_days) + num(slip.unpaid_leave_days) }} day(s) salary cut</span>
              </div>
              <span class="ps-row-amt ps-neg">- {{ money(slip.unpaid_deduction) }}</span>
            </div>

            <!-- Late penalty -->
            <div v-if="num(slip.late_count) > 0 || num(slip.late_deduction) > 0" class="ps-row">
              <div class="ps-row-left">
                <span class="ps-row-name">Late Penalty</span>
                <span class="ps-row-sub">
                  {{ num(slip.late_count) }} late ·
                  {{ num(slip.late_penalty_days) > 0 ? num(slip.late_penalty_days) + ' day penalty' : 'no penalty' }}
                </span>
              </div>
              <span class="ps-row-amt" :class="num(slip.late_deduction) > 0 ? 'ps-neg' : 'ps-muted'">
                {{ num(slip.late_deduction) > 0 ? '- ' : '' }}{{ money(slip.late_deduction) }}
              </span>
            </div>
          </div>
          <div class="ps-total">
            <span>Total Deductions</span>
            <span style="color:#dc2626;">{{ money(num(slip.total_deductions) + num(slip.unpaid_deduction) + num(slip.late_deduction)) }}</span>
          </div>
        </div>

        <!-- Net -->
       <div class="ps-net">
          <div>
            <div class="ps-net-lbl">Net Salary</div>
            <div class="ps-net-sub">Take-home for {{ period }}</div>
          </div>
          <span class="ps-net-amt">{{ money(slip.net_salary) }}</span>
        </div>

        <div class="ps-ctc">
          <span>Monthly CTC <b>{{ money(slip.ctc_monthly) }}</b></span>
          <span class="ps-dot"></span>
          <span>Gross <b>{{ money(slip.gross_salary) }}</b></span>
        </div>
      </template>

      <div v-else class="text-center py-5 text-muted" style="font-size:13px;">
        No payslip data.
      </div>
    </div>
  </div>
</template>

<style scoped>
.ps-oc { width: 500px; max-width: 94vw; }
.offcanvas-header { border-bottom: 1px solid #f1f5f9; padding: 18px 24px; }
.offcanvas-title { font-size: 16px; font-weight: 700; color: #0f172a; }
.ps-oc-ico { width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
  background: linear-gradient(135deg, #4f46e5, #7c3aed); color: #fff;
  display: inline-flex; align-items: center; justify-content: center; font-size: 18px; }
.offcanvas-body { padding: 24px; background: #fafbfd; }

/* Header */
.ps-head { text-align: center; padding: 4px 0 20px; margin-bottom: 20px; border-bottom: 1px dashed #e2e8f0; }
.ps-emp-name { font-size: 19px; font-weight: 800; color: #0f172a; letter-spacing: -.2px; }
.ps-emp-sub { font-size: 12.5px; color: #94a3b8; margin-top: 3px; }
.ps-period { display: inline-block; margin-top: 10px; font-size: 11.5px; font-weight: 600; color: #4f46e5; background: #eef2ff; padding: 4px 14px; border-radius: 20px; }

/* Attendance: 3 x 2 grid */
.ps-att { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 22px; }
.ps-att-item { background: #fff; border: 1px solid #eef2f7; border-radius: 12px; padding: 14px 8px; text-align: center; }
.ps-att-num { display: block; font-size: 20px; font-weight: 800; color: #0f172a; line-height: 1; }
.ps-att-lbl { display: block; margin-top: 6px; font-size: 10.5px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: .4px; }

/* Sections */
.ps-section { background: #fff; border: 1px solid #eef2f7; border-radius: 14px; padding: 14px 16px 6px; margin-bottom: 16px; }
.ps-sec-title { display: flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; padding: 8px 12px; border-radius: 8px; margin-bottom: 6px; }
.ps-sec-title i { font-size: 15px; }
.ps-earn { background: #dcfce7; color: #16a34a; }
.ps-deduct { background: #fee2e2; color: #dc2626; }

.ps-row { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding: 12px 8px; border-bottom: 1px solid #f1f5f9; }
.ps-rows .ps-row:last-child { border-bottom: none; }
.ps-row-left { display: flex; flex-direction: column; min-width: 0; }
.ps-row-name { font-size: 13.5px; color: #334155; font-weight: 500; }
.ps-row-sub { font-size: 11px; color: #94a3b8; margin-top: 2px; }
.ps-row-amt { font-size: 13.5px; font-weight: 600; color: #1e293b; white-space: nowrap; font-variant-numeric: tabular-nums; }
.ps-neg { color: #dc2626; }
.ps-muted { color: #94a3b8; }

.ps-total { display: flex; justify-content: space-between; align-items: center; margin: 4px -16px 0; padding: 13px 24px; background: #f8fafc; border-top: 1px solid #eef2f7; border-radius: 0 0 14px 14px; font-size: 13.5px; font-weight: 700; color: #0f172a; }

/* Net */
.ps-net { display: flex; justify-content: space-between; align-items: center; background: linear-gradient(135deg, #4f46e5, #7c3aed); border-radius: 16px; padding: 20px 22px; color: #fff; margin-top: 6px; box-shadow: 0 10px 24px -10px rgba(79,70,229,.55); }
.ps-net-lbl { font-size: 14px; font-weight: 700; }
.ps-net-sub { font-size: 11px; opacity: .75; margin-top: 3px; }
.ps-net-amt { font-size: 26px; font-weight: 800; letter-spacing: -.3px; }

/* CTC footer */
.ps-ctc { display: flex; align-items: center; justify-content: center; gap: 10px; font-size: 11.5px; color: #94a3b8; margin-top: 16px; }
.ps-ctc b { color: #64748b; font-weight: 600; margin-left: 3px; }
.ps-dot { width: 4px; height: 4px; border-radius: 50%; background: #cbd5e1; }
</style>